import {
  forwardRef,
  useState,
  useRef,
  useMemo,
  useEffect,
  useCallback,
  useImperativeHandle
} from 'react';
import type { PropsWithoutRef, KeyboardEvent, ClipboardEvent, FocusEvent } from 'react';
// Import the Slate editor factory.
import { createEditor, Node, Transforms, Editor } from 'slate';
import type { Element, Range, Text } from 'slate';
// Import the Slate components and React plugin.
import { Slate, Editable, withReact, ReactEditor } from 'slate-react';
import { withHistory } from 'slate-history';
import styled from 'styled-components';
import type { VirtualElement } from '@popperjs/core';

import {
  FormField,
  FormControl,
  StyledPopover,
  useTheme,
  useUID,
  createUID,
  debounce,
  Popover,
  Menu,
  useOuterEvent,
  useElement
} from '@pega/cosmos-react-core';
import type { ForwardRefForwardPropsComponent, MenuProps } from '@pega/cosmos-react-core';

import StyledRichTextEditor, { StyledEditorContainer } from './RichTextEditor.styles';
import Toolbar from './Toolbar/Toolbar';
import { slateNodeRenderer, slateLeafRenderer } from './utils/renderers';
import { onSearchChange } from './utils/search';
import EditorCommands from './utils/EditorCommands';
import ListCommands from './utils/ListCommands';
import { convertHtml } from './utils/htmlConverter';
import { convertSlateToHtml } from './utils/slateConverter';
import type { RichTextEditorProps } from './RichTextEditor.types';
import { RichTextEditorContext } from './RichTextEditor.context';
import type { RichTextEditorContextValue } from './RichTextEditor.context';
import { convertMarkdownForEditor } from './utils/markdownConverter';

const withVoids = (editor: Editor): ReactEditor => {
  editor.isVoid = element => {
    return element.type === 'image' || element.type === 'image-placeholder';
  };

  return editor as ReactEditor;
};

const StyledSearchPopover = styled(StyledPopover)`
  min-width: 20rem;
`;

const RichTextEditor: ForwardRefForwardPropsComponent<RichTextEditorProps> = forwardRef(
  function RichTextEditor(
    props: PropsWithoutRef<RichTextEditorProps>,
    ref: RichTextEditorProps['ref']
  ) {
    const {
      components: {
        'text-area': { 'min-height': minHeight }
      }
    } = useTheme();
    const uid = useUID();
    const labelId = useUID();
    const {
      id = uid,
      label,
      labelHidden,
      info,
      status,
      required,
      disabled,
      readOnly,
      additionalInfo,
      onChange,
      onFocus,
      onBlur,
      placeholder,
      toolbar,
      onImageAdded,
      markdownOnly,
      searchTriggers,
      onSearch,
      getSearchItemReplacement,
      searchRenderers,
      menu,
      height = { min: minHeight },
      defaultValue,
      autoFocus,
      ...restProps
    } = props;

    const formFieldRef = useRef<HTMLElement>(null);
    const [searchMenuPopoverEl, setSearchMenuPopoverEl] = useElement<HTMLDivElement>();
    const editor = useMemo(() => withVoids(withReact(withHistory(createEditor()))), []);
    const [value, setValue] = useState<(Element | Text)[]>(() =>
      defaultValue && !markdownOnly
        ? convertHtml(defaultValue)
        : convertMarkdownForEditor(defaultValue, searchRenderers)
    );
    const [focus, setFocus] = useState(autoFocus);
    const [toolbarPopover, setToolbarPopover] = useState(false);
    const [toolbarSticky, setToolbarSticky] = useState(false);
    const [focusableToolbarBtn, setFocusableToolbarBtn] = useState<HTMLElement | null>(null);

    const [popoverTarget, setPopoverTarget] = useState<Range | null>(null);
    const [searchEl, setSearchEl] = useState<VirtualElement | null>(null);

    const updateToolbarStyle = () => {
      if ((formFieldRef.current?.offsetHeight || 0) > window.innerHeight) {
        setToolbarSticky(true);
      } else {
        setToolbarSticky(false);
      }
    };

    const debouncedUpdateToolbarStyle = debounce(updateToolbarStyle, 200);

    const getPlainText = () => {
      return value
        .map(node => {
          if (markdownOnly && (node as Element).children) {
            return (node as Element).children
              .map(child => {
                let textNode = child;
                if ((child as Element).type === 'text') {
                  const children = (child as Element).children;
                  // When input is copied and pasted text nodes can be split into multiple texts, this handles that case
                  if (children.length === 1) {
                    textNode = children[0];
                  } else {
                    return children
                      .map(subChild => {
                        const childText = subChild as Text;
                        if (childText.searchResult) {
                          return childText.searchResult.markdown;
                        }
                        return childText.text;
                      })
                      .join('');
                  }
                }
                return (textNode as Text).searchResult?.markdown || (textNode as Text).text;
              })
              .join('');
          }
          return Node.string(node);
        })
        .join('\n');
    };

    const onEditorFocus: RichTextEditorContextValue['onEditorFocus'] = ({
      event,
      force,
      focusEditor,
      runAfterFocus
    }) => {
      if (focusEditor) {
        setTimeout(() => {
          if (!ReactEditor.isFocused(editor)) {
            ReactEditor.focus(editor);
            setFocus(true);

            if (!editor.selection) {
              Transforms.select(editor, {
                anchor: Editor.end(editor, [0]),
                focus: Editor.end(editor, [0])
              });
            }
            onFocus?.();
          }
        }, 0);
      }

      if (runAfterFocus && (force || (!disabled && !toolbarPopover))) {
        setTimeout(runAfterFocus, 0);
      } else {
        event?.stopPropagation();
      }
    };

    const blurEditor = (force?: boolean) => {
      if (force) ReactEditor.blur(editor);
      if (ReactEditor.isFocused(editor)) {
        onBlur?.();
      }
      setFocus(false);
      setPopoverTarget(null);
    };

    useOuterEvent('click', [formFieldRef.current, searchMenuPopoverEl], () => blurEditor());

    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          onEditorFocus({ focusEditor: true });
        },
        getPlainText: () => {
          return getPlainText();
        },
        getRichText: () => {
          return JSON.stringify(value);
        },
        getHtml: () => {
          return convertSlateToHtml(value);
        },
        clear: () => {
          const point = { path: [0, 0], offset: 0 };
          editor.selection = { anchor: point, focus: point };
          editor.children = [
            {
              type: 'paragraph',
              children: [{ text: '' }]
            }
          ];
          setValue(editor.children);
        },
        appendImage: ({ src, alt }, imageId) => {
          const path = EditorCommands.appendImage(editor, { src, alt }, imageId);
          if (path) {
            Transforms.select(editor, { anchor: path, focus: path });
            ReactEditor.focus(editor);
            setFocus(true);
          }
        },
        insertText: (text: string) => {
          editor.insertText(text);
        },
        insertHtml: (html: string, overwrite: boolean = false) => {
          if (overwrite) {
            const point = { path: [0, 0], offset: 0 };
            editor.selection = { anchor: point, focus: point };
            editor.children = convertHtml(html !== '' ? html : '<p></p>');
          } else {
            editor.children = [...value, ...convertHtml(html)];
          }
          setValue(editor.children);
        },
        insertMarkdown: (markdown: string) => {
          const nodes = convertMarkdownForEditor(markdown, searchRenderers);
          const point = { path: [0, 0], offset: 0 };
          editor.selection = { anchor: point, focus: point };
          editor.children = nodes;
          setValue(nodes);
        }
      }),
      [editor, value, searchRenderers]
    );

    const onMenuItemClick: NonNullable<MenuProps['onItemClick']> = (itemId, e) => {
      // MenuItem expects actual click events, but we don't have reference to actual menu item element to trigger a click.
      menu?.onItemClick?.(itemId, e);
      const replacement = getSearchItemReplacement?.(itemId);
      if (replacement) {
        let node: Node;

        if (replacement.markdown && replacement.component && replacement.props) {
          node = {
            text: replacement.text,
            searchResult: {
              markdown: replacement.markdown,
              component: replacement.component,
              props: replacement.props,
              // Prevents copying searchResult attribute onPaste
              toJSON: () => undefined
            }
          };
        } else {
          node = { text: replacement.text };
        }

        EditorCommands.replaceWithNodes(editor, popoverTarget, node);
        Transforms.insertNodes(editor, { text: ' ' });
      }
    };

    const onKeyDown = useCallback(
      (event: KeyboardEvent<HTMLElement>) => {
        if (popoverTarget && menu) {
          if (event.key === 'Escape') {
            event.preventDefault();
            setPopoverTarget(null);
          }
          if (['Enter', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
            event.preventDefault();
          }
        } else if (event.key === 'Tab' && !event.shiftKey) {
          if (focusableToolbarBtn) {
            focusableToolbarBtn.focus();
            event.preventDefault();
          }
        } else {
          ListCommands.checkListEnter(editor, event);
          EditorCommands.checkLinkBreak(editor, event);
          EditorCommands.checkSearchDownKeys(editor, event);
          EditorCommands.checkImageEnter(editor, event);
          EditorCommands.checkKeyCommands(editor, event, markdownOnly);
        }
      },
      [menu, popoverTarget, focusableToolbarBtn]
    );

    const onKeyUp = (event: KeyboardEvent<HTMLElement>) => {
      EditorCommands.checkSearchUpKeys(editor, event);
    };

    // Adds an image placeholder to be replaced later in time by an image
    const addImage = (file: File) => {
      const placeholderId = EditorCommands.appendImagePlaceholder(editor);
      if (placeholderId) onImageAdded?.(file, placeholderId);
    };

    const onPaste = (e: ClipboardEvent) => {
      const plainTextData = e.clipboardData?.getData('text/plain');

      if (plainTextData && e.clipboardData.items.length > 1) {
        e.preventDefault();

        const dt = new DataTransfer();
        dt.setData('text/plain', plainTextData);

        editor.insertData(dt);
        return;
      }

      let image;
      for (let i = 0; i < e.clipboardData.items.length; i += 1) {
        const item = e.clipboardData.items[i];

        if (item.type.startsWith('image')) {
          image = item.getAsFile();
        }
      }

      if (image) {
        e.preventDefault();
        addImage(image);
      }
    };

    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer) {
        Array.from(e.dataTransfer.files).forEach(file => {
          if (file.type.includes('image')) {
            addImage(file);
          }
        });
      }
    };

    const selectAll = () => {
      const elements = Array.from(Node.elements(editor));
      const firstPath = elements[0][1];
      const lastPath = elements[elements.length - 1][1];
      Transforms.select(editor, {
        anchor: Editor.start(editor, firstPath),
        focus: Editor.end(editor, lastPath)
      });
    };

    useEffect(() => {
      if (popoverTarget) {
        setSearchEl(ReactEditor.toDOMRange(editor, popoverTarget) as VirtualElement);
      }
    }, [popoverTarget]);

    useEffect(() => {
      onChange?.();
    }, [value]);

    useEffect(() => {
      if (autoFocus) {
        setTimeout(() => {
          ReactEditor.focus(editor);
          setFocus(true);
        }, 0);
      }
    }, []);

    return (
      <RichTextEditorContext.Provider
        // eslint-disable-next-line react/jsx-no-constructed-context-values
        value={{
          addImage,
          setPopoverOpen: setToolbarPopover,
          onEditorFocus,
          disabled: disabled || false
        }}
      >
        <FormField
          toolbar={!!(!markdownOnly && toolbar?.length)}
          label={
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
            <span onClick={() => onEditorFocus({ focusEditor: true })}>{label}</span>
          }
          labelHidden={labelHidden}
          labelFor=''
          labelId={labelId}
          id={id}
          info={info}
          status={status}
          required={required}
          disabled={disabled}
          additionalInfo={additionalInfo}
          readOnly={readOnly}
          onDrop={onDrop}
          onFocus={(e: FocusEvent) => {
            if (e.target === formFieldRef.current && !disabled) {
              onEditorFocus({ focusEditor: true });
            }
          }}
          ref={formFieldRef}
          aria-label={`${label} ${
            !markdownOnly && !readOnly && toolbar?.length ? 'Hit tab to edit toolbar below' : ''
          }`}
          tabIndex={disabled ? -1 : undefined}
        >
          <Slate
            editor={editor}
            value={value}
            onChange={newValue => {
              if (!disabled) {
                setValue(newValue);
                debouncedUpdateToolbarStyle();

                if (searchTriggers && onSearch && markdownOnly) {
                  const res = searchTriggers.flatMap(({ trigger, regex }) => {
                    for (let i = 0; i < 10; i += 1) {
                      try {
                        const groupId = createUID();
                        return [
                          {
                            trigger,
                            groupId: `g${groupId}`,
                            regex: new RegExp(
                              String.raw`(?:^|\s)(?<match>${trigger}(?<g${groupId}>${
                                regex || String.raw`\w*`
                              }))$`
                            )
                          }
                        ];
                      } catch (e: unknown) {
                        if (!(e instanceof SyntaxError)) throw e;
                      }
                    }

                    // Failsafe
                    return [];
                  });

                  let search;
                  for (let i = 0; i < res.length; i += 1) {
                    search = onSearchChange(editor, res[i]);
                    if (search) break;
                  }

                  if (search) {
                    setPopoverTarget(search.target);
                    onSearch({ search: search.search, trigger: search.trigger });
                  } else {
                    setPopoverTarget(null);
                  }
                }
              }
            }}
          >
            <FormControl
              {...restProps}
              ref={ref}
              ariaLabel={label}
              required={required}
              disabled={disabled}
              readOnly={readOnly}
              status={status}
              as={StyledRichTextEditor}
              onClick={() => {
                onEditorFocus({ focusEditor: true });
              }}
              focused={focus}
              tabIndex={readOnly ? 0 : undefined}
            >
              <StyledEditorContainer
                containerHeight={height}
                onClick={() => {
                  if (!disabled) {
                    onEditorFocus({ focusEditor: true });
                  }
                }}
              >
                <Editable
                  id={id}
                  renderElement={slateNodeRenderer}
                  renderLeaf={slateLeafRenderer}
                  onKeyDown={onKeyDown}
                  onKeyUp={onKeyUp}
                  placeholder={placeholder}
                  onFocus={(event: FocusEvent) => {
                    onEditorFocus({ focusEditor: true, event });
                  }}
                  onBlur={() => blurEditor()}
                  onDOMBeforeInput={e => {
                    // Detect and prevent double paste bug caused by CrowdStrike Falcon extension.
                    if (e.inputType === 'insertFromPaste' && !e.isTrusted) {
                      e.preventDefault();
                      return true;
                    }
                  }}
                  disabled={disabled}
                  readOnly={readOnly}
                  onPaste={onPaste}
                  tabIndex={disabled ? -1 : undefined}
                  onClick={e => {
                    if (e.detail === 3) {
                      e.preventDefault();
                      selectAll();
                    }
                  }}
                  aria-labelledby={labelId}
                />
              </StyledEditorContainer>
              {!markdownOnly && !readOnly && toolbar?.length ? (
                <Toolbar
                  features={toolbar}
                  sticky={toolbarSticky}
                  setFocusableBtn={el => {
                    setFocusableToolbarBtn(el);
                  }}
                />
              ) : null}
            </FormControl>
            <Popover
              ref={setSearchMenuPopoverEl}
              show={!!popoverTarget && !!menu}
              strategy='fixed'
              target={searchEl}
              placement='bottom-start'
            >
              {menu && (
                <Menu
                  {...menu}
                  as={StyledSearchPopover}
                  focusControlEl={
                    formFieldRef.current?.querySelector('[role="textbox"]') || undefined
                  }
                  mode='action'
                  onItemClick={onMenuItemClick}
                />
              )}
            </Popover>
          </Slate>
        </FormField>
      </RichTextEditorContext.Provider>
    );
  }
);

export default RichTextEditor;
